import { useState, useEffect } from "react";
import { getErrorMessage } from "@/lib/errors";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Progress } from "@/components/ui/progress";
import { supabase } from "@/integrations/supabase/client";
import { useToast } from "@/hooks/use-toast";
import { useUserPreferences } from "@/hooks/useUserPreferences";
import { Plus, Trash2, TrendingUp, Calendar } from "lucide-react";
import { motion } from "framer-motion";
import { format, startOfMonth, endOfMonth, startOfYear, endOfYear, eachDayOfInterval, isBefore, isAfter, parseISO } from "date-fns";
import { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer, Area, AreaChart } from "recharts";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";

const EXPENSE_CATEGORIES = [
  "Housing", "Transportation", "Food", "Utilities", "Healthcare",
  "Entertainment", "Shopping", "Education", "Savings", "Other"
];

interface Budget {
  id: string;
  category: string;
  amount: number;
  period?: "monthly" | "yearly"; // not a column on the live table; older rows may carry it
  period_start: string;
  period_end: string;
}

interface BudgetMilestone {
  id: string;
  budget_id: string;
  target_date: string;
  planned_amount: number;
}

interface BudgetsProps {
  embedded?: boolean;
}

const Budgets = ({ embedded = false }: BudgetsProps) => {
  const [budgets, setBudgets] = useState<Budget[]>([]);
  const [spending, setSpending] = useState<{[key: string]: number}>({});
  const [milestones, setMilestones] = useState<{[key: string]: BudgetMilestone[]}>({});
  const [category, setCategory] = useState("");
  const [amount, setAmount] = useState("");
  const [period, setPeriod] = useState<"monthly" | "yearly">("monthly");
  const [startDate, setStartDate] = useState(format(new Date(), "yyyy-MM-dd"));
  const [endDate, setEndDate] = useState("");
  const [loading, setLoading] = useState(true);
  const [budgetDialogOpen, setBudgetDialogOpen] = useState(false);
  const [selectedBudget, setSelectedBudget] = useState<Budget | null>(null);
  const [milestoneDialogOpen, setMilestoneDialogOpen] = useState(false);
  const [milestoneDate, setMilestoneDate] = useState("");
  const [milestoneAmount, setMilestoneAmount] = useState("");
  const { toast } = useToast();
  const { parseNumber, decimalSeparator, getPlaceholder, formatCurrency } = useUserPreferences();

  useEffect(() => {
    fetchBudgets();
    fetchSpending();
    fetchMilestones();
  }, []);

  const fetchBudgets = async () => {
    try {
      const { data, error } = await supabase
        .from("budgets")
        .select("*")
        .order("category");

      if (error) throw error;
      setBudgets((data || []) as Budget[]);
    } catch (error) {
      toast({
        title: "Error",
        description: getErrorMessage(error),
        variant: "destructive",
      });
    } finally {
      setLoading(false);
    }
  };

  const fetchSpending = async () => {
    try {
      const now = new Date();
      const monthStart = format(startOfMonth(now), "yyyy-MM-dd");
      const monthEnd = format(endOfMonth(now), "yyyy-MM-dd");

      const { data, error } = await supabase
        .from("transactions")
        .select("category, amount, date")
        .eq("type", "expense")
        .gte("date", monthStart)
        .lte("date", monthEnd);

      if (error) throw error;

      const spendingByCategory: {[key: string]: number} = {};
      (data || []).forEach((t: any) => {
        spendingByCategory[t.category] = (spendingByCategory[t.category] || 0) + Number(t.amount);
      });

      setSpending(spendingByCategory);
    } catch (error) {
      console.error("Error fetching spending:", error);
    }
  };

  const fetchMilestones = async () => {
    try {
      const { data, error } = await supabase
        .from("budget_milestones")
        .select("*")
        .order("target_date");

      if (error) throw error;

      const milestonesByBudget: {[key: string]: BudgetMilestone[]} = {};
      (data || []).forEach((m: any) => {
        if (!milestonesByBudget[m.budget_id]) {
          milestonesByBudget[m.budget_id] = [];
        }
        milestonesByBudget[m.budget_id].push(m);
      });

      setMilestones(milestonesByBudget);
    } catch (error) {
      console.error("Error fetching milestones:", error);
    }
  };

  const handleAddBudget = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!category || !amount) return;

    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) throw new Error("Not authenticated");

      const { error } = await supabase.from("budgets").insert({
        user_id: user.id,
        category,
        amount: parseNumber(amount),
        period_start: startDate,
        period_end: endDate || "2100-01-01",
      });

      if (error) throw error;

      toast({
        title: "Success",
        description: "Budget plan created successfully",
      });

      setBudgetDialogOpen(false);
      setCategory("");
      setAmount("");
      setStartDate(format(new Date(), "yyyy-MM-dd"));
      setEndDate("");
      fetchBudgets();
    } catch (error) {
      toast({
        title: "Error",
        description: getErrorMessage(error),
        variant: "destructive",
      });
    }
  };

  const handleAddMilestone = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!selectedBudget || !milestoneDate || !milestoneAmount) return;

    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) throw new Error("Not authenticated");

      const { error } = await supabase.from("budget_milestones").insert({
        budget_id: selectedBudget.id,
        user_id: user.id,
        title: `${selectedBudget.category} - ${format(parseISO(milestoneDate), "MMM yyyy")}`,
        target_date: milestoneDate,
        planned_amount: parseFloat(milestoneAmount),
      });

      if (error) throw error;

      toast({
        title: "Success",
        description: "Milestone added successfully",
      });

      setMilestoneDate("");
      setMilestoneAmount("");
      setMilestoneDialogOpen(false);
      fetchMilestones();
    } catch (error) {
      toast({
        title: "Error",
        description: getErrorMessage(error),
        variant: "destructive",
      });
    }
  };

  const openMilestoneDialog = (budget: Budget) => {
    setSelectedBudget(budget);
    setMilestoneDate("");
    setMilestoneAmount("");
    setMilestoneDialogOpen(true);
  };

  const handleDelete = async (id: string) => {
    try {
      const { error: milestonesError } = await supabase
        .from("budget_milestones")
        .delete()
        .eq("budget_id", id);
      if (milestonesError) throw milestonesError;

      const { error } = await supabase.from("budgets").delete().eq("id", id);
      if (error) throw error;

      toast({
        title: "Success",
        description: "Budget deleted",
      });
      fetchBudgets();
    } catch (error) {
      toast({
        title: "Error",
        description: getErrorMessage(error),
        variant: "destructive",
      });
    }
  };

  const getProgress = (budget: Budget) => {
    const spent = spending[budget.category] || 0;
    return Math.min((spent / budget.amount) * 100, 100);
  };

  const getChartData = (budget: Budget) => {
    const budgetMilestones = milestones[budget.id] || [];
    const spent = spending[budget.category] || 0;
    
    if (budgetMilestones.length === 0) {
      return [];
    }

    return budgetMilestones.map((milestone, index) => {
      const previousAmount = index > 0 ? budgetMilestones[index - 1].planned_amount : 0;
      return {
        date: format(parseISO(milestone.target_date), "MMM dd"),
        planned: Number(milestone.planned_amount),
        actual: spent,
      };
    });
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
      </div>
    );
  }

  return (
    <div className={embedded ? "" : "container mx-auto px-4 py-8"}>
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.4 }}
        className="space-y-8"
      >
        {!embedded ? (
          <div className="flex items-center justify-between">
            <div>
              <h1 className="text-3xl font-bold mb-2">Budget Plans & Tracking</h1>
              <p className="text-muted-foreground">Create spending plans and track progress against your goals</p>
            </div>
            <Button onClick={() => setBudgetDialogOpen(true)}>
              <Plus className="h-4 w-4 mr-2" />
              New Budget
            </Button>
          </div>
        ) : (
          <div className="flex justify-end">
            <Button onClick={() => setBudgetDialogOpen(true)}>
              <Plus className="h-4 w-4 mr-2" />
              New Budget
            </Button>
          </div>
        )}

        <div className="grid gap-6 md:grid-cols-2">
          {budgets.map((budget) => {
            const chartData = getChartData(budget);
            const hasMilestones = chartData.length > 0;
            
            return (
              <Card key={budget.id} className="bg-card/50 backdrop-blur">
                <CardHeader>
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-3">
                      <div className="h-10 w-10 rounded-full bg-primary/15 flex items-center justify-center">
                        <TrendingUp className="h-5 w-5 text-primary-text" />
                      </div>
                      <div>
                        <CardTitle className="text-lg">{budget.category}</CardTitle>
                        <p className="text-xs text-muted-foreground">
                          {format(parseISO(budget.period_start), "MMM dd, yyyy")}
                          {budget.period_end && ` - ${format(parseISO(budget.period_end), "MMM dd, yyyy")}`}
                        </p>
                      </div>
                    </div>
                    <div className="flex gap-2">
                      <Button 
                        variant="ghost" 
                        size="icon"
                        onClick={() => openMilestoneDialog(budget)}
                      >
                        <Calendar className="h-4 w-4" />
                      </Button>
                      <Button variant="ghost" size="icon" onClick={() => handleDelete(budget.id)}>
                        <Trash2 className="h-4 w-4" />
                      </Button>
                    </div>
                  </div>
                </CardHeader>
                <CardContent className="space-y-4">
                  {hasMilestones && (
                    <div className="h-48">
                      <ResponsiveContainer width="100%" height="100%">
                        <AreaChart data={chartData}>
                          <defs>
                            <linearGradient id={`planned-${budget.id}`} x1="0" y1="0" x2="0" y2="1">
                              <stop offset="5%" stopColor="hsl(var(--primary))" stopOpacity={0.3}/>
                              <stop offset="95%" stopColor="hsl(var(--primary))" stopOpacity={0}/>
                            </linearGradient>
                            <linearGradient id={`actual-${budget.id}`} x1="0" y1="0" x2="0" y2="1">
                              <stop offset="5%" stopColor="hsl(var(--expense))" stopOpacity={0.3}/>
                              <stop offset="95%" stopColor="hsl(var(--expense))" stopOpacity={0}/>
                            </linearGradient>
                          </defs>
                          <CartesianGrid strokeDasharray="3 3" stroke="hsl(var(--border))" opacity={0.3} />
                          <XAxis 
                            dataKey="date" 
                            tick={{ fill: 'hsl(var(--muted-foreground))', fontSize: 11 }}
                            stroke="hsl(var(--border))"
                          />
                          <YAxis 
                            tick={{ fill: 'hsl(var(--muted-foreground))', fontSize: 11 }}
                            stroke="hsl(var(--border))"
                          />
                          <Tooltip 
                            contentStyle={{
                              backgroundColor: 'hsl(var(--popover))',
                              border: '1px solid hsl(var(--border))',
                              borderRadius: '8px',
                            }}
                          />
                          <Area 
                            type="monotone" 
                            dataKey="planned" 
                            stroke="hsl(var(--primary))" 
                            fill={`url(#planned-${budget.id})`}
                            strokeWidth={2}
                            name="Planned"
                          />
                          <Area 
                            type="monotone" 
                            dataKey="actual" 
                            stroke="hsl(var(--expense))" 
                            fill={`url(#actual-${budget.id})`}
                            strokeWidth={2}
                            name="Actual"
                          />
                        </AreaChart>
                      </ResponsiveContainer>
                    </div>
                  )}
                  
                  <div className="space-y-2">
                    <div className="flex justify-between text-sm">
                      <span className="text-muted-foreground">
                        {formatCurrency(spending[budget.category] || 0)} of {formatCurrency(budget.amount)}
                      </span>
                      <span className={getProgress(budget) > 90 ? "text-destructive" : "text-muted-foreground"}>
                        {getProgress(budget).toFixed(0)}%
                      </span>
                    </div>
                    <Progress 
                      value={getProgress(budget)} 
                      className={getProgress(budget) > 90 ? "[&>div]:bg-destructive" : ""}
                    />
                  </div>
                  
                  <div className="flex items-center justify-between pt-2 border-t">
                    <p className="text-xs text-muted-foreground capitalize">{budget.period ? `${budget.period} budget` : "Budget"}</p>
                    {!hasMilestones && (
                      <Button 
                        variant="outline" 
                        size="sm"
                        onClick={() => openMilestoneDialog(budget)}
                      >
                        Add Milestones
                      </Button>
                    )}
                  </div>
                </CardContent>
              </Card>
            );
          })}
        </div>

        {budgets.length === 0 && (
          <Card>
            <CardContent className="py-12 flex flex-col items-center gap-4">
              <TrendingUp className="h-12 w-12 text-muted-foreground/50" />
              <div className="text-center">
                <p className="font-medium text-lg">No budget plans yet</p>
                <p className="text-sm text-muted-foreground">
                  Create your first budget plan to track spending goals with milestones
                </p>
              </div>
              <Button onClick={() => setBudgetDialogOpen(true)}>
                <Plus className="h-4 w-4 mr-2" />
                Create your first budget
              </Button>
            </CardContent>
          </Card>
        )}

      </motion.div>

      <Dialog open={budgetDialogOpen} onOpenChange={setBudgetDialogOpen}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle>Create Budget Plan</DialogTitle>
          </DialogHeader>
          <form onSubmit={handleAddBudget} className="space-y-4">
            <div className="space-y-2">
              <Label>Category</Label>
              <Select value={category} onValueChange={setCategory} required>
                <SelectTrigger>
                  <SelectValue placeholder="Select category" />
                </SelectTrigger>
                <SelectContent>
                  {EXPENSE_CATEGORIES.map((cat) => (
                    <SelectItem key={cat} value={cat}>{cat}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-2">
              <Label>Total Budget</Label>
              <Input
                type="text"
                inputMode="decimal"
                value={amount}
                onChange={(e) => setAmount(e.target.value)}
                placeholder={getPlaceholder()}
                required
                className="font-mono"
              />
              <p className="text-xs text-muted-foreground">Use "{decimalSeparator}" for decimals</p>
            </div>
            <div className="space-y-2">
              <Label>Period</Label>
              <Select value={period} onValueChange={(v: "monthly" | "yearly") => setPeriod(v)}>
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="monthly">Monthly</SelectItem>
                  <SelectItem value="yearly">Yearly</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-2">
              <Label>Start Date</Label>
              <Input
                type="date"
                value={startDate}
                onChange={(e) => setStartDate(e.target.value)}
                required
              />
            </div>
            <div className="space-y-2">
              <Label>End Date (Optional)</Label>
              <Input
                type="date"
                value={endDate}
                onChange={(e) => setEndDate(e.target.value)}
              />
            </div>
            <Button type="submit" className="w-full">
              <Plus className="h-4 w-4 mr-2" />
              Create Budget Plan
            </Button>
          </form>
        </DialogContent>
      </Dialog>

      <Dialog open={milestoneDialogOpen} onOpenChange={setMilestoneDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>
              Add Milestone {selectedBudget && `for ${selectedBudget.category}`}
            </DialogTitle>
          </DialogHeader>
          <form onSubmit={handleAddMilestone} className="space-y-4">
            <div className="space-y-2">
              <Label>Target Date</Label>
              <Input
                type="date"
                value={milestoneDate}
                onChange={(e) => setMilestoneDate(e.target.value)}
                min={selectedBudget?.period_start}
                max={selectedBudget?.period_end || undefined}
                required
              />
            </div>
            <div className="space-y-2">
              <Label>Planned Amount by this Date</Label>
              <Input
                type="number"
                step="0.01"
                min="0.01"
                value={milestoneAmount}
                onChange={(e) => setMilestoneAmount(e.target.value)}
                placeholder="1000"
                required
              />
              <p className="text-xs text-muted-foreground">
                Total budget: {selectedBudget ? formatCurrency(selectedBudget.amount) : formatCurrency(0)}
              </p>
            </div>
            <Button type="submit" className="w-full">
              <Plus className="h-4 w-4 mr-2" />
              Add Milestone
            </Button>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default Budgets;